
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Users, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ConvertToClientDialog } from '../ConvertToClientDialog';
import type { Lead } from '../types';

interface LeadHeaderProps {
  lead: Lead;
  onEdit: () => void;
  onDelete: () => void;
}

export const LeadHeader: React.FC<LeadHeaderProps> = ({ lead, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const [showConvertDialog, setShowConvertDialog] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'meeting': return 'bg-purple-100 text-purple-800';
      case 'proposal': return 'bg-orange-100 text-orange-800';
      case 'negotiating': return 'bg-amber-100 text-amber-800';
      case 'won': return 'bg-green-100 text-green-800';
      case 'lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isConverted = lead.converted_to_client_id;
  const canConvert = lead.status !== 'lost' && !isConverted;

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/leads')}
            className="flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Leads
          </Button>
          
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold">{lead.name}</h1>
              {isConverted && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Converted to Client
                </Badge>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Badge className={getStatusColor(lead.status)}>
                {lead.status?.charAt(0).toUpperCase() + lead.status?.slice(1)}
              </Badge>
              <Badge className={getPriorityColor(lead.priority)}>
                {lead.priority?.charAt(0).toUpperCase() + lead.priority?.slice(1)} Priority
              </Badge>
              {lead.industry && (
                <Badge variant="outline">{lead.industry}</Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canConvert && (
            <Button 
              onClick={() => setShowConvertDialog(true)}
              className="flex items-center gap-2"
            >
              <Users className="h-4 w-4" />
              Convert to Client
            </Button>
          )}
          
          {isConverted && lead.converted_to_client_id && (
            <Button 
              variant="outline"
              onClick={() => navigate(`/clients/${lead.converted_to_client_id}`)}
              className="flex items-center gap-2"
            >
              <Users className="h-4 w-4" />
              View Client
            </Button>
          )}
          
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Edit className="h-4 w-4" />
          </Button>
          
          <Button variant="outline" size="sm" onClick={onDelete}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ConvertToClientDialog 
        lead={lead}
        open={showConvertDialog}
        onOpenChange={setShowConvertDialog}
      />
    </>
  );
};

export default LeadHeader;
